import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './user.css';
import { FaArrowLeft, FaCheck, FaArrowCircleLeft, FaArrowCircleRight } from "react-icons/fa";
import 'bootstrap/dist/css/bootstrap.min.css';
import api from '../../../service/caller';
import { toast } from 'react-toastify';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';

const User = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [userDetails, setUserDetails] = useState({});
    const [scheduleData, setScheduleData] = useState(null);
    const [activeTab, setActiveTab] = useState('template');
    const [calendarView, setCalendarView] = useState('timeGridWeek');

    // Get user ID from URL parameters
    const queryParams = new URLSearchParams(location.search);
    const userId = queryParams.get('_id');

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const response = await api.get('api/users/' + userId);
                setUserDetails(response.data);
            } catch (error) {
                toast.error("Error fetching User Details");
                console.error("Error fetching User Details:", error);
            }
        };
        fetchUserDetails();
    }, [userId]);

    useEffect(() => {
        const fetchUserSchedule = async () => {
            try {
                const response = await api.get('api/schedule/' + userId);
                if (response?.data?.data && response.data.data.length > 0) {
                    setScheduleData(response.data.data[0]);
                }
            } catch (error) {
                toast.error("Error fetching User Schedule");
                console.error("Error fetching User Schedule:", error);
            }
        };
        fetchUserSchedule();
    }, [userId]);

    const handleUsersList = () => {
        navigate({
            pathname: '/hospitaladmin/stafflist'
        });
    };

    const getColorByPriority = (priority) => {
        switch (priority) {
            case 'high':
                return '#dc3545'; // Red for high priority
            case 'emergency':
                return '#ffc107'; // Yellow for emergency
            case 'flexible':
                return '#17a2b8'; // Teal for flexible
            default:
                return '#28a745'; // Green for regular
        }
    };

    // Convert template to events for FullCalendar
    const getEventsFromTemplate = () => {
        if (!scheduleData || !scheduleData.template) return [];

        const events = [];
        const days = {
            monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0
        };

        // Get current week's dates
        const currentDate = new Date();
        const currentDay = currentDate.getDay(); // 0 is Sunday, 1 is Monday, etc.
        const firstDayOfWeek = new Date(currentDate);
        firstDayOfWeek.setDate(currentDate.getDate() - (currentDay === 0 ? 6 : currentDay - 1)); // Adjust to get Monday

        Object.entries(scheduleData.template).forEach(([day, schedule]) => {
            if (schedule.active) {
                // Calculate the date for this day
                const eventDate = new Date(firstDayOfWeek);
                const dayNumber = days[day];
                eventDate.setDate(firstDayOfWeek.getDate() + (dayNumber === 0 ? 6 : dayNumber - 1));

                // Create event
                const startDateTime = new Date(eventDate);
                const [startHours, startMinutes] = schedule.start.split(':').map(Number);
                startDateTime.setHours(startHours, startMinutes, 0);

                const endDateTime = new Date(eventDate);
                const [endHours, endMinutes] = schedule.end.split(':').map(Number);
                endDateTime.setHours(endHours, endMinutes, 0);

                events.push({
                    title: 'Available',
                    start: startDateTime,
                    end: endDateTime,
                    backgroundColor: '#28a745',
                    extendedProps: {
                        doubleBooking: schedule.allowDoubleBooking
                    }
                });
            }
        });

        return events;
    };

    // All events combined (template-based and custom events)
    const getAllEvents = () => {
        if (!scheduleData) return [];

        const templateEvents = getEventsFromTemplate();

        // Get actual events from the schedule data
        const customEvents = scheduleData.events?.map(event => ({
            id: event._id,
            title: event.title,
            start: new Date(event.start),
            end: new Date(event.end),
            backgroundColor: getColorByPriority(event.priority),
            extendedProps: {
                type: event.type,
                priority: event.priority,
                specialty: event.specialty
            }
        })) || [];

        // Get emergency slots
        const emergencyEvents = scheduleData.emergencySlots?.map(slot => {
            const [year, month, day] = slot.date.split('-').map(Number);
            const [startHours, startMinutes] = slot.start.split(':').map(Number);
            const [endHours, endMinutes] = slot.end.split(':').map(Number);

            const startDate = new Date(year, month - 1, day, startHours, startMinutes);
            const endDate = new Date(year, month - 1, day, endHours, endMinutes);

            return {
                id: slot.id,
                title: 'Emergency Slot',
                start: startDate,
                end: endDate,
                backgroundColor: '#ffc107', // Yellow for emergency
                extendedProps: {
                    type: 'emergency'
                }
            };
        }) || [];

        return [...templateEvents, ...customEvents, ...emergencyEvents];
    };

    return (
        <div className="user-profile p-2 divp20">
            <div className='row p-3'>
                <div className='col-3 mx-auto'>
                    <button className="btn btn-primary rounded-pill mb-4" onClick={handleUsersList}>
                        <FaArrowLeft /> Retour
                    </button>
                    <div className="profile-header mt-3">
                        <div className="profile-image">
                            <img src='https://toppng.com/uploads/preview/icons-logos-emojis-user-icon-png-transparent-11563566676e32kbvynug.png' alt="profile" className="tete" />
                        </div>
                    </div>
                    <div className="lateral">
                        <div className="solid-line"></div>
                        <div className="dotted-line"></div>
                    </div>
                </div>
                <div className='col-9 mx-auto'>
                    <div className="profile-info mb-2">
                        <div className="row">
                            <div className="col-md-3">
                                <label>Nom:</label>
                                <p>{userDetails?.lastname}</p>
                            </div>
                            <div className="col-md-3">
                                <label>Prénom:</label>
                                <p>{userDetails?.firstname}</p>
                            </div>
                            <div className="col-md-4">
                                <label>Role:</label>
                                <p style={{ whiteSpace: 'normal' }}>{userDetails?.role?.name}</p>
                            </div>
                            <div className="col-md-2">
                                <label>Gender:</label>
                                <p className={userDetails?.civility ?? 'text-warning'}>{userDetails?.civility ?? 'Undefiened'}</p>
                            </div>
                            {/* <div className="col-md-3 my-3">
                                <label>Fonction Type:</label>
                                <p className={userDetails?.type ?? 'text-warning'}>{userDetails?.type ?? 'Undefiened'}</p>
                            </div> */}
                        </div>
                    </div>
                    <div className="department-section mt-2">
                        <h6>Department:</h6>
                        <div className="tags-container">
                            {Array.isArray(userDetails?.departementId) && userDetails.departementId.length > 0 ? (
                                userDetails.departementId.map((dept) => (
                                    <span key={dept._id} className="badge me-2">
                                        <span className="ami px-2"><FaCheck /></span>
                                        {dept.name}
                                    </span>
                                ))
                            ) : (
                                <p>No departments available</p>
                            )}
                        </div>
                    </div>
                    <div className="specialties-section mt-2">
                        <h6>Specialities:</h6>
                        <div className="tags-container">
                            {Array.isArray(userDetails?.specialties) && userDetails.specialties.length > 0 ? (
                                userDetails.specialties.map((speciality) => (
                                    <span key={speciality._id} className="badge me-2">
                                        <span className="ami px-2"><FaCheck /></span>
                                        {speciality.name}
                                    </span>
                                ))
                            ) : (
                                <p>No Specialties available</p>
                            )}
                        </div>
                    </div>

                    <div className="availability-section mt-4">
                        <h6>Disponibilités:</h6>

                        {scheduleData ? (
                            <div>
                                <ul className="nav nav-tabs mb-3">
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'template' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('template')}
                                        >
                                            Planning Hebdomadaire
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'calendar' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('calendar')}
                                        >
                                            Calendrier
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'emergency' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('emergency')}
                                        >
                                            Urgences
                                        </button>
                                    </li>
                                </ul>

                                {activeTab === 'template' && (
                                    <div className="weekly-template">
                                        <div className="row">
                                            {Object.entries(scheduleData.template || {}).map(([day, schedule]) => (
                                                <div key={day} className="col-md-2 m-3 time-slotDiv">
                                                    <div className="day-label text-center">
                                                        {day.charAt(0).toUpperCase() + day.slice(1)}
                                                    </div>
                                                    {schedule.active ? (
                                                        <>
                                                            <div className="time-slot">{schedule.start}</div>
                                                            <div className="text-center"> - </div>
                                                            <div className="time-slot">{schedule.end}</div>
                                                            {schedule.allowDoubleBooking && (
                                                                <div className="mt-2 text-info">
                                                                    Double réservation autorisée
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="text-center text-muted">Non disponible</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'calendar' && (
                                    <div className="calendar-view">
                                        <select
                                            className="form-select mb-3"
                                            value={calendarView}
                                            onChange={(e) => setCalendarView(e.target.value)}
                                        >
                                            <option value="dayGridMonth">Vue mensuelle</option>
                                            <option value="timeGridWeek">Vue hebdomadaire</option>
                                            <option value="timeGridDay">Vue journalière</option>
                                        </select>

                                        <FullCalendar
                                            plugins={[dayGridPlugin, timeGridPlugin]}
                                            initialView={calendarView}
                                            headerToolbar={{
                                                left: 'prev,next today',
                                                center: 'title',
                                                right: 'dayGridMonth,timeGridWeek,timeGridDay'
                                            }}
                                            events={getAllEvents()}
                                            height="auto"
                                        />

                                        <div className="mt-3">
                                            <h6>Légende des couleurs:</h6>
                                            <div className="d-flex flex-wrap">
                                                <div className="me-3 mb-2">
                                                    <span className="badge bg-success me-1">&nbsp;</span>
                                                    Disponibilités régulières
                                                </div>
                                                <div className="me-3 mb-2">
                                                    <span className="badge bg-info me-1">&nbsp;</span>
                                                    Rendez-vous flexibles
                                                </div>
                                                <div className="me-3 mb-2">
                                                    <span className="badge bg-warning me-1">&nbsp;</span>
                                                    Créneaux d'urgence
                                                </div>
                                                <div className="me-3 mb-2">
                                                    <span className="badge bg-danger me-1">&nbsp;</span>
                                                    Créneaux prioritaires
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'emergency' && (
                                    <div className="emergency-slots">
                                        {scheduleData.emergencySlots && scheduleData.emergencySlots.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Heure de début</th>
                                                            <th>Heure de fin</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {scheduleData.emergencySlots.map(slot => (
                                                            <tr key={slot.id}>
                                                                <td>{slot.date}</td>
                                                                <td>{slot.start}</td>
                                                                <td>{slot.end}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="alert alert-info">
                                                Aucun créneau d'urgence défini
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="alert alert-warning">Pas de planning pour l'instant</div>
                        )}
                    </div>

                    <div className="contact-section mt-4">
                        <h5>Contact informations</h5>
                        <div className="row">
                            <div className="col-md-5">
                                <label>E-mail:</label>
                                <p>{userDetails?.email}</p>
                            </div>
                            <div className="col-md-3">
                                <label>Phone:</label>
                                <p>{userDetails?.contact?.phone}</p>
                            </div>
                            <div className="col-md-4">
                                <label>Address:</label>
                                <p>{userDetails?.contact?.address}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default User;